import { z } from 'zod';
import { AdsAccountDetail, AdsAccountListResponse, AdsAccountProfile, AdsAsset, AdsBatch, AdsBatchListResponse, AdsBatchPreview, AdsBatchRepeatInput, AdsBatchSpendResponse, AdsCampaignDuplicateResponse, AdsDuplicateBlockedDetails, AdsDuplicateView, AdsCampaignFunnelSettings, AdsCampaignSearchResponse, AdsCountryList, AdsCountryListResponse, AdsEntityLabelsResponse, AdsFromCampaignResponse, AdsFunnelSettingsCatalog, AdsHealthResponse, AdsInterestListResponse, AdsLaunchDetail, AdsLaunchRetryResponse, AdsLaunchRollbackResponse, AdsLocalesResponse, AdsMedia, AdsMediaListResponse, AdsMediaSourcesResponse, AdsNamingSampleResponse, AdsPostListResponse, AdsPostResolveResponse, AdsRequestLogResponse, AdsRegistryResponse, AdsTemplate, AdsTemplateListResponse, AdsUploadCompleteResponse, AdsUploadInitResponse, AdsUploadPartsResponse, AdsUsedInterestsResponse, AdsValueIssue, } from '@wit-support/admin-contracts';
import { createModuleApi, ModuleRequestError, moduleRequestErrorFrom, } from '@wit-support/admin-client-core';
const api = createModuleApi({ errorFormat: 'server-message' });
export function setAdsLauncherApiBaseUrl(url) {
    api.setBaseUrl(url);
}
/** Абсолютный адрес admin-api (для `<img src>`). */
export function adsApiUrl(path) {
    return `${api.baseUrl()}${path}`;
}
/**
 * `<img src>` превью: `previewUrl` ответа — путь admin-api (`/api/ads/...`,
 * его подставил admin-api вместо пути прокси auto-ads); null — превью нет.
 */
export function previewSrc(previewUrl) {
    return previewUrl ? adsApiUrl(previewUrl) : null;
}
/*
 * Ошибки раздела — общий `ModuleRequestError` (`admin-client-core`):
 * - `status` — HTTP-статус admin-api;
 * - `code` — `error` тела (`upstream_rejected`, `upstream_unavailable`,
 *   `ads_not_configured`, `ads_builder_disabled`, `forbidden`, …);
 * - `upstreamCode` — код auto-ads (`preview-stale`,
 *   `template-version-conflict`, `verify-has-errors`, `rate-limited`, …);
 * - `serverMessage` — русская фраза admin-api;
 * - `issues` — проблемы значений по полям реестра (400 `invalid-values`),
 *   типизированно — `adsIssuesOf`;
 * - `details` — остальные поля отказа (`currentVersion`, `batchId`,
 *   `previewHash`, `missing`, `retryAfterSec`, …).
 */
const IssueList = z.array(AdsValueIssue);
/** `issues` ошибки по контракту раздела; чужая форма или не та ошибка — `[]`. */
export function adsIssuesOf(err) {
    if (!(err instanceof ModuleRequestError))
        return [];
    const parsed = IssueList.safeParse(err.issues);
    return parsed.success ? parsed.data : [];
}
/** Текст ошибки для человека: фраза admin-api, иначе код (`http_502` → `HTTP 502`). */
export function adsErrorText(err) {
    if (err.serverMessage !== null)
        return err.serverMessage;
    return /^http_\d+$/.test(err.code) ? `HTTP ${err.status}` : err.code;
}
/*
 * Номер попытки для журнала (v4 §9, §11.7): каждое ИЗМЕНЕНИЕ уходит с
 * заголовком `X-Request-Id` (свой номер на вызов) — admin-api пишет его в
 * строку журнала как `client_request_id`, а экран журнала находит строку по
 * `?requestId=`. Отказ запоминает номер: тост ошибки ведёт [Подробнее в
 * журнале] ровно на эту попытку (`requestIdOf`).
 */
const ERROR_REQUEST_ID = new WeakMap();
/** Номер попытки, на которой упал вызов раздела; null — не изменение или не наша ошибка. */
export function requestIdOf(err) {
    return typeof err === 'object' && err !== null ? (ERROR_REQUEST_ID.get(err) ?? null) : null;
}
function headerOf(init, name) {
    const h = init?.headers;
    if (h == null || h instanceof Headers || Array.isArray(h))
        return null;
    const v = h[name];
    return typeof v === 'string' ? v : null;
}
async function failed(r, init) {
    const err = await moduleRequestErrorFrom(r);
    const id = headerOf(init, 'x-request-id');
    if (id)
        ERROR_REQUEST_ID.set(err, id);
    throw err;
}
async function request(schema, path, init) {
    const r = await api.fetchRaw(path, init);
    if (!r.ok)
        return failed(r, init);
    return schema.parse(await r.json());
}
async function requestVoid(path, init) {
    const r = await api.fetchRaw(path, init);
    if (!r.ok)
        await failed(r, init);
}
/** Номер попытки: латиница/цифры, 8–100 символов (как ждёт admin-api). */
function newRequestId() {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === 'function')
        return `ui-${c.randomUUID()}`;
    let s = 'ui-';
    for (let i = 0; i < 32; i++)
        s += Math.floor(Math.random() * 36).toString(36);
    return s;
}
function jsonInit(method, body) {
    return {
        method,
        headers: { 'content-type': 'application/json', 'x-request-id': newRequestId() },
        body: JSON.stringify(body),
    };
}
function qs(params) {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null || v === '')
            continue;
        p.set(k, typeof v === 'boolean' ? (v ? '1' : '0') : String(v));
    }
    const s = p.toString();
    return s ? `?${s}` : '';
}
const BASE = '/api/ads';
const enc = encodeURIComponent;
// --- health / registry ------------------------------------------------------
export function getAdsHealth() {
    return request(AdsHealthResponse, `${BASE}/health`);
}
export function getAdsRegistry() {
    return request(AdsRegistryResponse, `${BASE}/registry`);
}
/** Пример имён редактируемого шаблона — по правилам auto-ads (своей копии в UI нет). */
export function getNamingSample(req) {
    return request(AdsNamingSampleResponse, `${BASE}/registry/naming-sample`, jsonInit('POST', req));
}
// --- настройки воронки (план K6) -------------------------------------------
/**
 * Каталог настроек воронки: подписи, виды, варианты. Недоступен — 502
 * `upstream_rejected` с `upstreamCode: 'funnel-settings-catalog-unavailable'`.
 */
export function getFunnelSettingsCatalog() {
    return request(AdsFunnelSettingsCatalog, `${BASE}/funnel-settings/catalog`);
}
/** Настройки воронки запуска или кампании Meta: текущая версия + история (только чтение). */
export function getCampaignFunnelSettings(q) {
    return request(AdsCampaignFunnelSettings, `${BASE}/funnel-settings${qs(q)}`);
}
// --- журнал ----------------------------------------------------------------
/** Журнал попыток (SB-2). Выключен на сервере — 404 `ads_request_log_disabled`. */
export function listRequestLog(q = {}) {
    return request(AdsRequestLogResponse, `${BASE}/request-log${qs(q)}`);
}
/** Журнал выключен на сервере (`ADS_REQUEST_LOG_ENABLED=false`): 404 `ads_request_log_disabled`. */
export function isRequestLogDisabled(err) {
    return (err instanceof ModuleRequestError &&
        err.status === 404 &&
        err.code === 'ads_request_log_disabled');
}
// --- templates --------------------------------------------------------------
export function listTemplates(q = {}) {
    return request(AdsTemplateListResponse, `${BASE}/templates${qs({ includeArchived: q.includeArchived ? '1' : undefined, q: q.q })}`);
}
export function getTemplate(id) {
    return request(AdsTemplate, `${BASE}/templates/${enc(id)}`);
}
export function createTemplate(input) {
    return request(AdsTemplate, `${BASE}/templates`, jsonInit('POST', input));
}
export function updateTemplate(id, input) {
    return request(AdsTemplate, `${BASE}/templates/${enc(id)}`, jsonInit('PUT', input));
}
export function setTemplateArchived(id, archived) {
    return request(AdsTemplate, `${BASE}/templates/${enc(id)}/${archived ? 'archive' : 'unarchive'}`, jsonInit('POST', {}));
}
export function duplicateTemplate(id, name) {
    return request(AdsTemplate, `${BASE}/templates/${enc(id)}/duplicate`, jsonInit('POST', { name }));
}
export function templateFromCampaign(req) {
    return request(AdsFromCampaignResponse, `${BASE}/templates/from-campaign`, jsonInit('POST', req));
}
/**
 * «Дублировать кампанию» (K2): кампания Meta → одноразовый шаблон-копия и готовый вход пачки.
 * 422 `duplicate-blocked` — шаблон не создан, причины разбирает `duplicateBlockedOf`.
 */
export function duplicateCampaign(campaignId, req) {
    return request(AdsCampaignDuplicateResponse, `${BASE}/campaigns/${enc(campaignId)}/duplicate`, jsonInit('POST', req));
}
/**
 * Копия кампании, открытая с сервера (K2b): что сохранила подготовка, готовый вход пачки
 * (ключ выдаёт сервер) и пачки, уже созданные из копии. Копию мог подготовить агент или другая
 * вкладка — браузер ничего не хранит.
 */
export function getDuplicate(templateId) {
    return request(AdsDuplicateView, `${BASE}/duplicates/${enc(templateId)}`);
}
/** Блокеры и fidelity отказа `duplicate-blocked`; не он (или чужая форма) — null. */
export function duplicateBlockedOf(err) {
    if (!(err instanceof ModuleRequestError) || err.upstreamCode !== 'duplicate-blocked')
        return null;
    const parsed = AdsDuplicateBlockedDetails.safeParse(err.details);
    return parsed.success ? parsed.data : null;
}
// --- accounts ---------------------------------------------------------------
// Строку и готовность кабинета собирает admin-api (список кабинетов сервиса +
// профили конструктора): `readiness.ok = missing = [] ∧ profile.enabled ∧
// platformEnabled`.
export function listAccounts() {
    return request(AdsAccountListResponse, `${BASE}/accounts`);
}
export function getAccount(pid, refresh = false) {
    return request(AdsAccountDetail, `${BASE}/accounts/${enc(pid)}${qs({ refresh: refresh ? '1' : undefined })}`);
}
export function putAccountProfile(pid, input) {
    return request(AdsAccountProfile, `${BASE}/accounts/${enc(pid)}/profile`, jsonInit('PUT', input));
}
// --- pickers ----------------------------------------------------------------
/**
 * Посты кабинетов (≤ 20) одним списком: один кабинет — `platformAccountId`,
 * несколько — `platformAccountIds=a,b` (строки несут совпадение страницы по
 * каждому кабинету).
 */
export function listPosts({ platformAccountIds, ...rest }) {
    const accounts = platformAccountIds.length === 1
        ? { platformAccountId: platformAccountIds[0] }
        : { platformAccountIds: platformAccountIds.join(',') };
    return request(AdsPostListResponse, `${BASE}/posts${qs({ ...accounts, ...rest })}`);
}
export function resolvePost(platformAccountId, input) {
    return request(AdsPostResolveResponse, `${BASE}/posts/resolve${qs({ platformAccountId, input })}`);
}
/**
 * Кампании для пикера — поиск по базе auto-ads (Meta не вызывается): `q` из
 * цифр — префикс id, иначе подстрока названия; `ids` — точные id (≤ 50,
 * подписи выбранных; режет хук `useCampaignsByIds`); без кабинетов — все.
 */
export function searchCampaigns({ platformAccountIds, ids, statuses, ...rest }) {
    const list = (v) => (v && v.length > 0 ? v.join(',') : undefined);
    return request(AdsCampaignSearchResponse, `${BASE}/campaigns${qs({
        platformAccountIds: list(platformAccountIds),
        ids: list(ids),
        statuses: list(statuses),
        ...rest,
    })}`);
}
/** Названия языков Meta; пресеты и запрещённые id — в реестре (`getRegistry`). */
/**
 * Названия сущностей Meta по ссылкам `вид:id` (≤ 200 за раз — режет хук
 * `useEntityLabels`). Ответ — в порядке запроса, без повторов.
 */
export function getEntityLabels(platformAccountId, refs) {
    return request(AdsEntityLabelsResponse, `${BASE}/labels${qs({ platformAccountId, ids: refs.join(',') })}`);
}
export function listLocales() {
    return request(AdsLocalesResponse, `${BASE}/locales`);
}
export function searchInterests(platformAccountId, q) {
    return request(AdsInterestListResponse, `${BASE}/targeting/interests${qs({ platformAccountId, q })}`);
}
/**
 * «Уже использовали»: интересы наших групп объявлений во всех кабинетах, по
 * убыванию числа групп. Без Meta (агрегат auto-ads), кабинет не нужен.
 */
export function listUsedInterests(opts = {}) {
    return request(AdsUsedInterestsResponse, `${BASE}/interests/used${qs(opts)}`);
}
// --- media ------------------------------------------------------------------
/**
 * Начало загрузки: ссылки на части в S3 — или `{deduplicated: true, asset}`,
 * если файл с тем же sha256 уже в медиатеке.
 */
export function initUpload(input) {
    return request(AdsUploadInitResponse, `${BASE}/media/uploads`, jsonInit('POST', input));
}
/** Свежие ссылки на части (подпись истекла). */
export function signUploadParts(uploadId, partNumbers) {
    return request(AdsUploadPartsResponse, `${BASE}/media/uploads/${enc(uploadId)}/parts`, jsonInit('POST', partNumbers ? { partNumbers } : {}));
}
export function completeUpload(uploadId, parts) {
    return request(AdsUploadCompleteResponse, `${BASE}/media/uploads/${enc(uploadId)}/complete`, jsonInit('POST', { parts }));
}
export function abortUpload(uploadId) {
    return requestVoid(`${BASE}/media/uploads/${enc(uploadId)}`, { method: 'DELETE' });
}
/** Один выбор медиа: загруженные / уже в рекламе / библиотека кабинета. */
export function listMediaSources(q) {
    return request(AdsMediaSourcesResponse, `${BASE}/media/sources${qs(q)}`);
}
/** «Загруженные» с копиями по кабинетам (страница «Медиатека»). */
export function listMedia(q = {}) {
    return request(AdsMediaListResponse, `${BASE}/media${qs(q)}`);
}
export function getAsset(assetId) {
    return request(AdsAsset, `${BASE}/media/assets/${enc(assetId)}`);
}
/** В архив: файл и все его копии; оригинал удаляется. */
export function archiveAsset(assetId) {
    return request(AdsAsset, `${BASE}/media/assets/${enc(assetId)}/archive`, jsonInit('POST', {}));
}
/** Повторить неудачный перенос файла в один кабинет (`id` — копия в кабинете). */
export function retryMedia(id) {
    return request(AdsMedia, `${BASE}/media/${enc(id)}/retry`, jsonInit('POST', {}));
}
// --- batches ----------------------------------------------------------------
export function previewBatch(input) {
    return request(AdsBatchPreview, `${BASE}/batches/preview`, jsonInit('POST', input));
}
/**
 * Деньги в день и форма плана до «Проверить» (v4 §3.11 п. 2): тот же вход, что
 * у preview (тот же ключ идемпотентности → те же уникальные центы); ничего не
 * пишет, посты и медиа не проверяет. Только при `features.builderV4`.
 */
export function batchSpend(input) {
    return request(AdsBatchSpendResponse, `${BASE}/batches/spend`, jsonInit('POST', input));
}
export function createBatch(input) {
    return request(AdsBatch, `${BASE}/batches`, jsonInit('POST', input));
}
export function listBatches(q = {}) {
    return request(AdsBatchListResponse, `${BASE}/batches${qs(q)}`);
}
export function getBatch(id) {
    return request(AdsBatch, `${BASE}/batches/${enc(id)}`);
}
/**
 * Черновик — выполнить; пачка в режиме исполнения — возобновить (упавшие,
 * зависшие, ждущие перепланирования). `launchIds` — только эти запуски.
 */
export function executeBatch(id, body = {}) {
    return request(AdsBatch, `${BASE}/batches/${enc(id)}/execute`, jsonInit('POST', body));
}
export function cancelBatch(id, body = {}) {
    return request(AdsBatch, `${BASE}/batches/${enc(id)}/cancel`, jsonInit('POST', body));
}
/**
 * «Создать такую же» (v4 §7): вход старой пачки (или одного её запуска),
 * сверенный с текущим шаблоном, деньги «было → станет» и заметки. Ничего не
 * пишет. Только при `features.builderV4`.
 */
export function getBatchRepeatInput(batchId, launchId) {
    return request(AdsBatchRepeatInput, `${BASE}/batches/${enc(batchId)}/repeat-input${qs({ launchId: launchId ?? undefined })}`);
}
// --- launches ---------------------------------------------------------------
export function getLaunch(id) {
    return request(AdsLaunchDetail, `${BASE}/launches/${enc(id)}`);
}
/**
 * Включить запуск. `force` — при расхождениях сверки (владелец);
 * `acknowledgeLateStart` — СЕЙЧАС, хотя старт уже прошёл (v4 §8.1, «Включить
 * сейчас»): сервер принимает флаг только у опоздавшего запуска.
 */
export function activateLaunch(id, opts = false) {
    const o = typeof opts === 'boolean' ? { force: opts } : opts;
    return request(AdsLaunchDetail, `${BASE}/launches/${enc(id)}/activate`, jsonInit('POST', {
        ...(o.force ? { force: true } : {}),
        ...(o.acknowledgeLateStart ? { acknowledgeLateStart: true } : {}),
    }));
}
export function pauseLaunch(id) {
    return request(AdsLaunchDetail, `${BASE}/launches/${enc(id)}/pause`, jsonInit('POST', {}));
}
export function retryLaunch(id) {
    return request(AdsLaunchRetryResponse, `${BASE}/launches/${enc(id)}/retry`, jsonInit('POST', {}));
}
export function rollbackLaunch(id, body = {}) {
    return request(AdsLaunchRollbackResponse, `${BASE}/launches/${enc(id)}/rollback`, jsonInit('POST', body));
}
export function reverifyLaunch(id) {
    return request(AdsLaunchDetail, `${BASE}/launches/${enc(id)}/reverify`, jsonInit('POST', {}));
}
// --- country lists ----------------------------------------------------------
export function listCountryLists() {
    return request(AdsCountryListResponse, `${BASE}/country-lists`);
}
export function createCountryList(input) {
    return request(AdsCountryList, `${BASE}/country-lists`, jsonInit('POST', input));
}
export function updateCountryList(id, patch) {
    return request(AdsCountryList, `${BASE}/country-lists/${enc(id)}`, jsonInit('PUT', patch));
}
export function deleteCountryList(id) {
    return requestVoid(`${BASE}/country-lists/${enc(id)}`, { method: 'DELETE' });
}
//# sourceMappingURL=api.js.map